import React from 'react';
import './gpj-pages.css';

const COURT_LOCATOR = 'https://www.ontario.ca/locations/courts/';
const ETIQUETTE_GUIDE = 'https://www.ontariocourts.ca/scj/guides-and-service-resources/virtual-courtroom-etiquette/';

const COURT_TIPS = [
  {
    category: 'Professional attire',
    tips: [
      'Dress properly for court in clean, respectful clothing',
      'Remove hats and sunglasses inside the courtroom unless needed for religious, medical, or accessibility reasons',
      'Check your hearing notice to confirm whether you are attending in person or virtually'
    ]
  },
  {
    category: 'Courtroom etiquette',
    tips: [
      'Be respectful and polite to everyone in the courtroom',
      'Address the judge as “Your Honour” or as directed by the court',
      'Stand when the judge enters or leaves and when speaking, unless instructed otherwise',
      'Do not interrupt another person who is speaking'
    ]
  },
  {
    category: 'Before your hearing',
    tips: [
      'Confirm the date, time, location, and attendance format on your notice',
      'Plan to arrive or connect early enough to handle security or technical checks',
      'Organise the filed court materials you are permitted to rely on',
      'Bring a way to take notes and write down directions or next steps'
    ]
  },
  {
    category: 'During the hearing',
    tips: [
      'Speak clearly, at a moderate pace, and only when it is your turn',
      'Focus on the issues before the court and the material properly in the record',
      'Answer the question asked; say when you do not know or do not understand',
      'Follow the judge’s directions about electronic devices and recording'
    ]
  }
];

const DOCUMENT_CHECKLIST = [
  'Government-issued photo identification',
  'Your hearing notice and court file number',
  'Copies of the court documents filed for this event',
  'Financial documents required for the event, if applicable',
  'Any permitted exhibits, orders, endorsements, or reports',
  'Contact information for the parties and any scheduled witnesses',
  'Notebook and pen',
  'Device, charger, and connection details for a virtual or hybrid hearing'
];

const COURTS = [
  {
    name: 'Family Court Branch of the Superior Court of Justice',
    description: 'Often called the Unified Family Court. In its locations, it can hear all types of family-law cases.'
  },
  {
    name: 'Superior Court of Justice',
    description: 'Hears divorce and property matters, as well as parenting and support claims connected with those cases.'
  },
  {
    name: 'Ontario Court of Justice',
    description: 'Hears many parenting, support, adoption, and child-protection matters, but not divorce or property division.'
  }
];

export default function CourtGuidePage() {
  return (
    <section className="gpj-section" aria-labelledby="court-guide-title">
      <div className="gpj-wrap">
        <div className="gpj-sec-head">
          <span className="gpj-eyebrow">Court guide</span>
          <h2 id="court-guide-title">Preparing for family court</h2>
          <p>
            A practical checklist for attending an Ontario family-court event. Always follow
            your hearing notice and any directions from the court handling your case.
          </p>
        </div>

        <div className="gpj-guide-grid">
          {COURT_TIPS.map((section) => (
            <article className="gpj-card" key={section.category}>
              <span className="gpj-card__kicker">Court preparation</span>
              <h3>{section.category}</h3>
              <ul>
                {section.tips.map((tip) => <li key={tip}>{tip}</li>)}
              </ul>
            </article>
          ))}
        </div>

        <div className="gpj-guide-panels">
          <section className="gpj-guide-panel" aria-labelledby="document-checklist-title">
            <h3 id="document-checklist-title">Document checklist</h3>
            <p>Use this as a preparation aid and compare it with the requirements for your specific event.</p>
            <div className="gpj-checklist">
              {DOCUMENT_CHECKLIST.map((item) => (
                <label key={item}>
                  <input type="checkbox" />
                  <span>{item}</span>
                </label>
              ))}
            </div>
            <button type="button" className="gpj-btn gpj-btn--navy gpj-btn--sm gpj-print-button" onClick={() => window.print()}>
              Print checklist
            </button>
          </section>

          <section className="gpj-guide-panel" aria-labelledby="court-locations-title">
            <h3 id="court-locations-title">Court locations</h3>
            <p>Ontario family matters are heard in different courts depending on the location and type of case.</p>
            <div className="gpj-court-types">
              {COURTS.map((court) => (
                <div key={court.name}>
                  <h4>{court.name}</h4>
                  <p>{court.description}</p>
                </div>
              ))}
            </div>
            <a className="gpj-btn gpj-btn--primary gpj-btn--sm" href={COURT_LOCATOR} target="_blank" rel="noopener noreferrer">
              Find an Ontario court ↗
            </a>
          </section>
        </div>

        <aside className="gpj-court-note">
          <div>
            <span className="gpj-card__kicker">Official guidance</span>
            <h3>What to expect in court</h3>
            <p>
              Court is a formal setting. Be prepared, stay respectful, listen carefully, and
              follow directions from the judge and court staff. Court practices can differ for
              in-person, virtual, and hybrid events.
            </p>
          </div>
          <a className="gpj-btn gpj-btn--onnavy gpj-btn--sm" href={ETIQUETTE_GUIDE} target="_blank" rel="noopener noreferrer">
            Read official etiquette guidance ↗
          </a>
        </aside>

        <p className="gpj-disclaimer">
          <b>This is general procedural information, not legal advice.</b> Your hearing notice,
          court orders, and directions from court staff or the judge control what you must do.
        </p>
      </div>
    </section>
  );
}
